import { useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";
import {
  Badge,
  Button,
  Card,
  Logomark,
  Screen,
  Text,
  useThemedStyles,
} from "~/components/ui";
import { useStarter } from "~/hooks/useStarter";
import { spacing, type ThemeColors } from "~/styles/tokens";

const QUICK_TIPS = [
  { id: "1", emoji: "🌡️", text: "Keep starter at 70–75°F for best activity" },
  { id: "2", emoji: "💧", text: "Use filtered or room-temp water" },
  { id: "3", emoji: "⚖️", text: "1:1:1 ratio = starter : flour : water" },
  { id: "4", emoji: "👃", text: "Ripe starter smells tangy, like yogurt" },
  { id: "5", emoji: "🕐", text: "Feed every 12–24h at room temperature" },
];

export default function HomeScreen() {
  const router = useRouter();
  const { starter, hoursUntilNextFeed } = useStarter();

  const styles = useStyles();
  const hours = hoursUntilNextFeed();
  const isOverdue = hours !== null && hours < 0;
  const isDueSoon = hours !== null && hours >= 0 && hours < 4;

  const starterStatus = () => {
    if (!starter) return null;
    if (isOverdue) {
      return (
        <Badge
          label={`Overdue by ${Math.abs(Math.round(hours!))}h`}
          variant="error"
        />
      );
    }
    if (isDueSoon)
      return (
        <Badge label={`Feed in ${Math.round(hours!)}h`} variant="warning" />
      );
    return (
      <Badge label={`Fed · ${Math.round(hours!)}h left`} variant="success" />
    );
  };

  return (
    <Screen scroll>
      <View style={styles.header}>
        <View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <Logomark size={28} />
            <Text variant="heading">DoughBro</Text>
          </View>
          <Text variant="caption">Your sourdough sidekick</Text>
        </View>
      </View>

      {starter ? (
        <Card elevation="lifted" style={styles.starterCard}>
          <View style={styles.starterHeader}>
            <Text variant="title" numberOfLines={1} style={styles.starterName}>
              🫙 {starter.name}
            </Text>
            {starterStatus()}
          </View>
          <Text variant="caption" style={styles.starterMeta}>
            Last fed{" "}
            {new Date(starter.lastFedAt).toLocaleString(undefined, {
              weekday: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Text>
          <Button
            label="Open starter"
            onPress={() => router.push("/starter")}
            variant="secondary"
            size="sm"
            style={styles.starterBtn}
          />
        </Card>
      ) : (
        <Card elevation="lifted" style={styles.starterCard}>
          <Text variant="title">🫙 No starter yet</Text>
          <Text variant="body" style={styles.starterMeta}>
            Set up your sourdough starter to get feeding reminders.
          </Text>
          <Button
            label="Set up starter"
            onPress={() => router.push("/starter")}
            variant="primary"
            size="sm"
            style={styles.starterBtn}
          />
        </Card>
      )}

      <Text variant="label" style={styles.sectionLabel}>
        Quick tips
      </Text>
      <Card elevation="raised" padding="xs">
        {QUICK_TIPS.map((tip, index) => (
          <View
            key={tip.id}
            style={[
              styles.tipRow,
              index < QUICK_TIPS.length - 1 && styles.tipRowBorder,
            ]}
          >
            <Text style={styles.tipEmoji}>{tip.emoji}</Text>
            <Text variant="body" style={styles.tipText}>
              {tip.text}
            </Text>
          </View>
        ))}
      </Card>
    </Screen>
  );
}

const useStyles = () => useThemedStyles(makeStyles);

const makeStyles = (c: ThemeColors) =>
  StyleSheet.create({
    header: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
      paddingTop: spacing.lg,
      paddingBottom: spacing.lg,
    },
    starterCard: {
      marginBottom: spacing.lg,
    },
    starterHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.sm,
      marginBottom: spacing.xs,
    },
    starterName: {
      flexShrink: 1,
    },
    starterMeta: {
      marginBottom: spacing.md,
    },
    starterBtn: {
      alignSelf: "flex-start",
    },
    sectionLabel: {
      marginBottom: spacing.sm,
      marginLeft: spacing.xs,
    },
    tipRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
      paddingVertical: spacing.sm + 2,
      paddingHorizontal: spacing.sm,
    },
    tipRowBorder: {
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    tipEmoji: {
      fontSize: 20,
      width: 26,
      textAlign: "center",
    },
    tipText: {
      flex: 1,
      color: c.heading,
    },
  });
